/**
 * Onderdelen Overzicht - Toont alle geïnstalleerde onderdelen
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiFetch from '../../services/api';
import ErrorDialog from '../../components/ErrorDialog';
import { useAuth } from '../../context/AuthContext';
import './Onderdelen.css';

interface BootOnderdeel {
  id: number;
  boot_id: number;
  voorraad_item_id: number | null;
  voorraad_groep_id: number | null;
  onderdeel_type: string;
  fabrikant: string | null;
  model: string | null;
  leverancier: string | null;
  artikelnummer: string | null;
  omschrijving: string | null;
  // Sinds de samenvoeging met de motoren mag dit leeg zijn: een motor die al in de
  // boot zat heeft vaak geen bekende inbouwdatum.
  positie: string | null;
  serienummer: string | null;
  installatie_datum: string | null;
  draaiuren_huidig: number | null;
  levensduur_uren: number | null;
  levensduur_maanden: number | null;
  inkoopprijs: number | null;
  vervangingsdatum: string | null;
  is_vervangen: boolean;
  aantal_voorraad?: number;
  min_voorraad?: number | null;
  status?: string;
}

// Categorieën mapping
const CATEGORIEEN: Record<string, string[]> = {
  // Romp
  romp: ['Romp', 'Dek', 'Kuip', 'Railing', 'Fenders', 'Touwwerk', 'Anker', 'Ankerlier', 'Luiken', 'Patrijspoorten', 'Antifouling', 'Anode', 'Opbouw', 'Aanleggerei', 'Bolder', 'Kikker', 'Klamp'],
  romp_antifouling: ['Antifouling'],
  romp_anoden: ['Anode'],
  romp_dek: ['Dek', 'Opbouw', 'Kuip', 'Luiken', 'Patrijspoorten', 'Railing'],
  romp_aanleggerei: ['Aanleggerei', 'Fenders', 'Touwwerk', 'Anker', 'Ankerlier', 'Bolder', 'Kikker', 'Klamp'],

  // Besturing
  besturing: ['Roer', 'Stuurwiel', 'Stuurkabel', 'Hydraulische besturing', 'Autopilot', 'Trim tabs', 'Boegschroef', 'Hekschroef', 'Stuurpen', 'Helmstok'],
  besturing_roer: ['Roer', 'Roersysteem', 'Roerblad', 'Roerkoning'],
  besturing_stuur: ['Stuurwiel', 'Stuurkabel', 'Hydraulische besturing', 'Stuurpen', 'Helmstok', 'Autopilot'],
  besturing_trim: ['Trim tabs', 'Boegschroef', 'Hekschroef', 'Triminrichting'],

  // Aandrijving
  aandrijving: ['Motor', 'Oliefilter', 'Brandstoffilter', 'Impeller', 'V-snaar', 'Motorolie', 'Keerkoppelingsolie', 'Luchtfilter', 'Koelvloeistof', 'Bougie', 'Anode', 'Schroefas', 'Schroef', 'Keerkoppeling', 'Saildrive', 'Schroefaslager', 'Schroefaspakking', 'Schroefaskoker', 'Brandstoftank', 'Brandstofleiding', 'Brandstoffilter', 'Brandstofsysteem'],
  aandrijving_hoofdmotor: ['Motor', 'Oliefilter', 'Brandstoffilter', 'Impeller', 'V-snaar', 'Motorolie', 'Keerkoppelingsolie', 'Luchtfilter', 'Koelvloeistof', 'Bougie', 'Anode', 'Hoofdmotor'],
  aandrijving_voortstuwing: ['Schroefas', 'Schroef', 'Keerkoppeling', 'Saildrive', 'Schroefaslager', 'Schroefaspakking', 'Schroefaskoker', 'Voortstuwingslijn'],
  aandrijving_brandstof: ['Brandstoftank', 'Brandstofleiding', 'Brandstoffilter', 'Brandstofsysteem'],

  // Stroom
  stroom: ['Accu', 'Dynamo', 'Walstroomaansluiting', 'Omvormer', 'Acculader', 'Zonnepaneel', 'Windgenerator', 'Zekeringkast', 'Schakelaar', 'Generator', 'Distributie'],
  stroom_accus: ['Accu', 'Batterij', 'Startaccu', 'Huishoudaccu'],
  stroom_laden: ['Acculader', 'Omvormer', 'Walstroomaansluiting', 'Zonnepaneel', 'Windgenerator', 'Dynamo'],
  stroom_distributie: ['Zekeringkast', 'Schakelaar', 'Distributie', 'Hoofdschakelaar', 'Zekering'],
  stroom_generator: ['Generator', 'Aggregaat'],

  // Water
  water: ['Waterpomp', 'Watertank', 'Waterfilter', 'Boiler', 'Drukvat', 'Waterleiding', 'Vuilwatertank', 'Vuilwaterpomp', 'Bilgepomp', 'Lenspomp'],
  water_veiligheid: ['Bilgepomp', 'Lenspomp', 'Veiligheidspompen'],
  water_drinkwater: ['Waterpomp', 'Watertank', 'Waterfilter', 'Boiler', 'Drukvat', 'Drinkwatersysteem'],
  water_wasafvoer: ['Waterleiding', 'Afvoer', 'Was- en Afvoersysteem'],
  water_afvalwater: ['Vuilwatertank', 'Vuilwaterpomp', 'Afvalwater', 'Septictank'],

  // Navigatie
  navigatie: ['GPS', 'Kaartplotter', 'Radar', 'Dieptemeter', 'Kompas', 'AIS', 'VHF Radio', 'Marifoon', 'Snelheidsmeter', 'Windmeter', 'Navigatieverlichting'],
  navigatie_positie: ['GPS', 'Kaartplotter', 'Radar', 'Kompas', 'AIS', 'Positiebepaling'],
  navigatie_communicatie: ['VHF Radio', 'Marifoon', 'Communicatie', 'SSB Radio'],
  navigatie_metingen: ['Dieptemeter', 'Snelheidsmeter', 'Windmeter', 'Log', 'Metingen'],
  navigatie_verlichting: ['Navigatieverlichting', 'Toplicht', 'Boordlicht', 'Heklicht', 'Ankerverlichting'],

  // Veiligheid
  veiligheid: ['Brandblusser', 'Reddingsvest', 'Reddingsboei', 'EPIRB', 'Noodpakket', 'Rookmelder', 'Gasdetector', 'Misthoorn'],
  veiligheid_redding: ['Reddingsvest', 'Reddingsboei', 'Reddingsvlot', 'EPIRB', 'Reddingsmiddelen'],
  veiligheid_brand: ['Brandblusser', 'Rookmelder', 'Gasdetector', 'Brandpreventie', 'Brandblussysteem'],
  veiligheid_nood: ['Noodpakket', 'Misthoorn', 'Nooduitrusting', 'Noodsignaal', 'Noodflare'],

  // Klimaat
  klimaat: ['Airconditioning', 'Verwarming', 'Ventilator', 'Dehumidifier', 'Kachel', 'Isolatie', 'Koeling'],
  klimaat_verwarming: ['Verwarming', 'Kachel', 'Diesel verwarming', 'Convector'],
  klimaat_koeling: ['Airconditioning', 'Koeling', 'Dehumidifier'],
  klimaat_ventilatie: ['Ventilator', 'Ventilatie', 'Isolatie', 'Dorade'],

  // Interieur
  interieur: ['Toilet', 'Koelkast', 'Fornuis', 'Oven', 'Wasbak', 'Douche', 'Televisie', 'Radio', 'Verlichting', 'Meubels', 'Kombuis', 'Sanitair', 'Meubilair', 'Entertainment'],
  interieur_kombuis: ['Koelkast', 'Fornuis', 'Oven', 'Kombuis', 'Kooktoestel'],
  interieur_sanitair: ['Toilet', 'Wasbak', 'Douche', 'Sanitair'],
  interieur_meubilair: ['Meubels', 'Meubilair', 'Betimmering', 'Kast', 'Bank', 'Tafel'],
  interieur_entertainment: ['Televisie', 'Radio', 'Entertainment', 'Geluidsinstallatie', 'Verlichting']
};

const OnderdelenList = () => {
  const [onderdelen, setOnderdelen] = useState<BootOnderdeel[]>([]);
  const [filteredOnderdelen, setFilteredOnderdelen] = useState<BootOnderdeel[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [stickyHeaderVisible, setStickyHeaderVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const { hasPermission } = useAuth();

  const categorie = searchParams.get('categorie');

  useEffect(() => {
    loadData();
  }, [categorie]);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setStickyHeaderVisible(rect.bottom <= 185);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Filter onderdelen op basis van zoekterm
    if (searchTerm.trim() === '') {
      setFilteredOnderdelen(onderdelen);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = onderdelen.filter(onderdeel =>
        onderdeel.onderdeel_type.toLowerCase().includes(term) ||
        (onderdeel.fabrikant && onderdeel.fabrikant.toLowerCase().includes(term)) ||
        (onderdeel.model && onderdeel.model.toLowerCase().includes(term)) ||
        (onderdeel.leverancier && onderdeel.leverancier.toLowerCase().includes(term)) ||
        (onderdeel.artikelnummer && onderdeel.artikelnummer.toLowerCase().includes(term))
      );
      setFilteredOnderdelen(filtered);
    }
  }, [searchTerm, onderdelen]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Laad ALLE onderdelen in één keer
      const alleOnderdelen: BootOnderdeel[] = await apiFetch('/api/v1/boot-onderdelen/all?include_vervangen=false');

      // Filter op categorie indien geselecteerd
      let gefilterd = alleOnderdelen;
      if (categorie && CATEGORIEEN[categorie]) {
        const categorieTypes = CATEGORIEEN[categorie];
        gefilterd = alleOnderdelen.filter(o =>
          categorieTypes.some(type =>
            o.onderdeel_type.toLowerCase().includes(type.toLowerCase())
          )
        );
      }

      setOnderdelen(gefilterd);
    } catch (err: any) {
      setError(err.message || 'Fout bij laden van onderdelen');
    } finally {
      setLoading(false);
    }
  };

  /* De negen systemen, in de volgorde waarin ze in CATEGORIEEN staan. */
  const HOOFDSYSTEMEN = ['romp', 'besturing', 'aandrijving', 'stroom', 'water',
                         'navigatie', 'veiligheid', 'klimaat', 'interieur'];

  const getCategorieNaam = (sleutel?: string) => {
    const key = sleutel ?? categorie;
    if (!key) return 'Alle Onderdelen';
    const namen: Record<string, string> = {
      // Romp
      romp: 'Romp',
      romp_antifouling: 'Romp - Antifouling',
      romp_anoden: 'Romp - Anoden',
      romp_dek: 'Romp - Dek en Opbouw',
      romp_aanleggerei: 'Romp - Aanleggerei',

      // Besturing
      besturing: 'Besturing',
      besturing_roer: 'Besturing - Roersysteem',
      besturing_stuur: 'Besturing - Stuurinrichting',
      besturing_trim: 'Besturing - Triminrichting',

      // Aandrijving
      aandrijving: 'Aandrijving',
      aandrijving_hoofdmotor: 'Aandrijving - Hoofdmotor',
      aandrijving_voortstuwing: 'Aandrijving - Voortstuwingslijn',
      aandrijving_brandstof: 'Aandrijving - Brandstofsysteem',

      // Stroom
      stroom: 'Stroom',
      stroom_accus: 'Stroom - Accu\'s',
      stroom_laden: 'Stroom - Laden & Omvormen',
      stroom_distributie: 'Stroom - Distributie',
      stroom_generator: 'Stroom - Generator',

      // Water
      water: 'Water',
      water_veiligheid: 'Water - Veiligheidspompen',
      water_drinkwater: 'Water - Drinkwatersysteem',
      water_wasafvoer: 'Water - Was- en Afvoersysteem',
      water_afvalwater: 'Water - Afvalwater',

      // Navigatie
      navigatie: 'Navigatie',
      navigatie_positie: 'Navigatie - Positiebepaling',
      navigatie_communicatie: 'Navigatie - Communicatie',
      navigatie_metingen: 'Navigatie - Metingen',
      navigatie_verlichting: 'Navigatie - Verlichting',

      // Veiligheid
      veiligheid: 'Veiligheid',
      veiligheid_redding: 'Veiligheid - Reddingsmiddelen',
      veiligheid_brand: 'Veiligheid - Brandpreventie',
      veiligheid_nood: 'Veiligheid - Nooduitrusting',

      // Klimaat
      klimaat: 'Klimaat',
      klimaat_verwarming: 'Klimaat - Verwarming',
      klimaat_koeling: 'Klimaat - Koeling',
      klimaat_ventilatie: 'Klimaat - Ventilatie',

      // Interieur
      interieur: 'Interieur',
      interieur_kombuis: 'Interieur - Kombuis',
      interieur_sanitair: 'Interieur - Sanitair',
      interieur_meubilair: 'Interieur - Meubilair',
      interieur_entertainment: 'Interieur - Entertainment'
    };
    return namen[key] || 'Onderdelen';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>{getCategorieNaam()}</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          Onderdelen laden...
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{getCategorieNaam()}</h1>
        {hasPermission('onderdelen_aanmaken') && (
          <button
            className="btn-primary"
            onClick={() => navigate('/onderdelen/new')}
          >
            + Nieuw Onderdeel
          </button>
        )}
      </div>

      {/* Zoekbalk en Filter dropdown */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center' }}>
        <div className="search-bar" style={{ flex: 1, marginBottom: 0 }}>
          <input
            type="text"
            placeholder="Zoek op type, fabrikant, model, leverancier of artikelnummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-results">
            {filteredOnderdelen.length} {filteredOnderdelen.length === 1 ? 'resultaat' : 'resultaten'}
          </span>
        </div>
        {/* Filtert deze lijst met gemonteerde onderdelen. Sprong eerder naar de
            catalogus-pagina's /aandrijving, /besturing enzovoort - een andere tabel,
            en die pagina's bestaan sinds 25-09-2026 niet meer. */}
        <select
          value={categorie || ''}
          onChange={(e) => {
            navigate(e.target.value ? `/onderdelen?categorie=${e.target.value}` : '/onderdelen');
          }}
          style={{
            padding: '0.625rem 1rem',
            fontSize: '0.9375rem',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            backgroundColor: 'white',
            color: '#1e293b',
            cursor: 'pointer',
            minWidth: '200px'
          }}
        >
          <option value="">Alle systemen</option>
          {HOOFDSYSTEMEN.map(sleutel => (
            <option key={sleutel} value={sleutel}>
              {getCategorieNaam(sleutel)}
            </option>
          ))}
        </select>
      </div>

      {filteredOnderdelen.length === 0 && onderdelen.length === 0 ? (
        <div style={{
          padding: '2rem',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p>Geen onderdelen gevonden{categorie ? ` in categorie "${getCategorieNaam()}"` : ''}.</p>
          {hasPermission('onderdelen_aanmaken') && (
            <button
              className="btn-primary"
              onClick={() => navigate('/onderdelen/new')}
              style={{ marginTop: '1rem' }}
            >
              Voeg eerste onderdeel toe
            </button>
          )}
        </div>
      ) : filteredOnderdelen.length === 0 ? (
        <div style={{
          padding: '2rem',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p>Geen onderdelen gevonden met zoekterm "{searchTerm}"</p>
        </div>
      ) : (
        <div className="table-container">
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {/* Fixed Header */}
            <div
              ref={headerRef}
              style={{
                display: 'flex',
                width: '100%',
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                fontWeight: 600,
                fontSize: '14px',
                color: '#495057',
                padding: '0.75rem 1rem',
                alignItems: 'center'
              }}
            >
              <div style={{ flex: '1 1 120px' }}>Type</div>
              <div style={{ flex: '1 1 120px' }}>Fabrikant</div>
              <div style={{ flex: '1 1 120px' }}>Model</div>
              <div style={{ flex: '1 1 120px' }}>Leverancier</div>
              <div style={{ flex: '0 0 110px' }}>Artikelnr.</div>
              <div style={{ flex: '0 0 90px' }}>Voorraad</div>
              <div style={{ flex: '0 0 90px' }}>Status</div>
              <div style={{ flex: '0 0 360px' }}>Acties</div>
            </div>

            {/* Sticky Duplicate Header */}
            {stickyHeaderVisible && (
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#495057',
                  padding: '0.75rem 1rem',
                  alignItems: 'center',
                  position: 'fixed',
                  top: '144px',
                  zIndex: 999,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ flex: '1 1 120px' }}>Type</div>
                <div style={{ flex: '1 1 120px' }}>Fabrikant</div>
                <div style={{ flex: '1 1 120px' }}>Model</div>
                <div style={{ flex: '1 1 120px' }}>Leverancier</div>
                <div style={{ flex: '0 0 110px' }}>Artikelnr.</div>
                <div style={{ flex: '0 0 90px' }}>Voorraad</div>
                <div style={{ flex: '0 0 90px' }}>Status</div>
                <div style={{ flex: '0 0 360px' }}>Acties</div>
              </div>
            )}

            {/* Data Rows */}
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              {filteredOnderdelen.map(onderdeel => (
                <div
                  key={onderdeel.id}
                  style={{
                    display: 'flex',
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderBottom: '1px solid #dee2e6',
                    alignItems: 'center',
                    backgroundColor: '#fff',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                >
                  <div style={{ flex: '1 1 120px' }}>{onderdeel.onderdeel_type}</div>
                  <div style={{ flex: '1 1 120px' }}>{onderdeel.fabrikant || '-'}</div>
                  <div style={{ flex: '1 1 120px' }}>{onderdeel.model || '-'}</div>
                  <div style={{ flex: '1 1 120px' }}>{onderdeel.leverancier || '-'}</div>
                  <div style={{ flex: '0 0 110px' }}>{onderdeel.artikelnummer || '-'}</div>
                  <div style={{ flex: '0 0 90px', fontWeight: 500 }}>{onderdeel.aantal_voorraad ?? '-'}</div>
                  <div style={{ flex: '0 0 90px' }}>
                    {onderdeel.status ? (
                      <span className={`status-badge status-${onderdeel.status}`}>
                        {onderdeel.status === 'kritiek' ? 'Kritiek' : onderdeel.status === 'waarschuwing' ? 'Laag' : 'OK'}
                      </span>
                    ) : '-'}
                  </div>
                  <div style={{ flex: '0 0 360px', display: 'flex', gap: '0.5rem' }}>
                    {hasPermission('onderdelen_bewerken') && (
                      <button
                        className="btn-small btn-secondary"
                        onClick={() => {
                          if (onderdeel.voorraad_groep_id) {
                            navigate(`/voorraad/groep/${onderdeel.voorraad_groep_id}`);
                          } else {
                            alert('Geen voorraad groep gekoppeld aan dit onderdeel');
                          }
                        }}
                        disabled={!onderdeel.voorraad_groep_id}
                      >
                        Bewerken
                      </button>
                    )}
                    <button
                      className="btn-small btn-primary"
                      onClick={() => {
                        if (onderdeel.voorraad_item_id) {
                          navigate(`/onderdelen/${onderdeel.voorraad_item_id}/documenten`);
                        } else {
                          alert('Geen voorraad item gekoppeld aan dit onderdeel');
                        }
                      }}
                      disabled={!onderdeel.voorraad_item_id}
                    >
                      Documenten
                    </button>
                    {hasPermission('onderdelen_verwijderen') && (
                      <button
                        className="btn-small btn-danger"
                        onClick={async () => {
                          if (!window.confirm(`Weet je zeker dat je "${onderdeel.onderdeel_type}" wilt verwijderen?`)) return;
                          try {
                            await apiFetch(`/api/v1/boot-onderdelen/${onderdeel.id}`, { method: 'DELETE' });
                            setOnderdelen(prev => prev.filter(o => o.id !== onderdeel.id));
                          } catch (err: any) {
                            setError(err.message || 'Fout bij verwijderen van onderdeel');
                          }
                        }}
                      >
                        Verwijderen
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: '#f0f9ff',
        borderRadius: '8px',
        fontSize: '0.9rem'
      }}>
        <strong>Totaal:</strong> {searchTerm ? `${filteredOnderdelen.length} van ${onderdelen.length}` : onderdelen.length} onderdelen gevonden
      </div>

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default OnderdelenList;
